// Pure helpers for the redesigned ContactPage.
//
// JSX-free + DOM-free so the node pool can unit-test the formatting
// + activity bucketing without booting the React layer. Types are
// duplicated here (rather than imported from ../api) for the same
// reason the pulse-page-helpers do it: api.ts pulls in Vite + DOM
// fetch types that the root tsconfig used by the node pool doesn't
// carry.

import {
  isRetentionTouchType,
  touchTypeChannel,
} from "@shared/activities/touch-types";

export type ContactActivityKind =
  | "all"
  | "communications"
  | "meetings"
  | "documents"
  | "notes"
  | "system";

// Tabs shown above the activity timeline in the order Grant's
// prototype renders them. Keep `all` first; the others mirror the
// internal `activity_type` taxonomy on the worker.
export const CONTACT_ACTIVITY_TABS: ReadonlyArray<{
  kind: ContactActivityKind;
  label: string;
}> = [
  { kind: "all", label: "All" },
  { kind: "communications", label: "Communications" },
  { kind: "meetings", label: "Meetings" },
  { kind: "documents", label: "Documents" },
  { kind: "notes", label: "Notes" },
  { kind: "system", label: "System" },
];

// Map a worker-returned activity_type to one of the tab kinds. Any
// type the tabs don't know about lands in "system" so the contact
// page never silently drops a row.
export function bucketForActivityType(
  activityType: string,
): ContactActivityKind {
  const t = activityType.toLowerCase();
  // The seven retention touches first — same fix as the household page
  // (Ryan, 2026-09-17). Without this they fall through to "system".
  if (isRetentionTouchType(t)) {
    return touchTypeChannel(t) === "meeting" ? "meetings" : "communications";
  }
  // Communications: anything carried over phone, email, text, etc.
  if (
    t === "email" ||
    t === "email_in" ||
    t === "email_out" ||
    t === "email_sent" ||
    t === "email_received" ||
    t === "call" ||
    t === "phone" ||
    t === "outbound_call" ||
    t === "inbound_call" ||
    t === "text" ||
    t === "sms" ||
    t === "letter"
  ) {
    return "communications";
  }
  if (
    t === "meeting" ||
    t === "appointment" ||
    t === "appointment_completed" ||
    t === "meeting_completed" ||
    t === "meeting_scheduled"
  ) {
    return "meetings";
  }
  if (
    t === "document_signed" ||
    t === "document_uploaded" ||
    t === "document_sent" ||
    t === "doc"
  ) {
    return "documents";
  }
  if (t === "note" || t === "note_added") {
    return "notes";
  }
  return "system";
}

// Filter an activity list to a given tab. The `all` tab is the
// identity transform — render every activity.
export function filterActivities<
  A extends { activity_type: string },
>(activities: ReadonlyArray<A>, kind: ContactActivityKind): A[] {
  if (kind === "all") return activities.slice();
  return activities.filter(
    (a) => bucketForActivityType(a.activity_type) === kind,
  );
}

// "Last contact" derivation. The contact hero shows "Last contact:
// 12 days ago · Joy Schroeder via phone" — pick the most recent
// communication-bucket activity, fall back to "No recorded contact"
// when none exist.
export interface LastContactSummary {
  daysAgo: number;
  channel: string; // "phone" / "email" / "text" / "letter" / "—"
  byUserId: string | null;
  occurredAtIso: string;
}

export function deriveLastContact<
  A extends {
    occurred_at: string;
    activity_type: string;
    user_id: string | null;
  },
>(
  activities: ReadonlyArray<A>,
  today: Date = new Date(),
): LastContactSummary | null {
  const comms = activities.filter(
    (a) => bucketForActivityType(a.activity_type) === "communications",
  );
  if (comms.length === 0) return null;
  // Latest by occurred_at descending.
  const sorted = comms
    .slice()
    .sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : -1));
  const latest = sorted[0]!;
  const occurred = new Date(latest.occurred_at);
  const ms = today.getTime() - occurred.getTime();
  const daysAgo = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  return {
    daysAgo,
    channel: channelLabelForActivityType(latest.activity_type),
    byUserId: latest.user_id,
    occurredAtIso: latest.occurred_at,
  };
}

function channelLabelForActivityType(activityType: string): string {
  const t = activityType.toLowerCase();
  if (
    t === "email" ||
    t === "email_in" ||
    t === "email_out" ||
    t === "email_sent" ||
    t === "email_received"
  ) {
    return "email";
  }
  if (
    t === "call" ||
    t === "phone" ||
    t === "outbound_call" ||
    t === "inbound_call"
  ) {
    return "phone";
  }
  if (t === "text" || t === "sms") return "text";
  if (t === "letter") return "letter";
  return "—";
}

// "12 days ago" / "yesterday" / "today" / "3 weeks ago" / "5 months
// ago". Used by the hero "Last contact:" line + the activity
// timeline date column. Tries to keep the language Grant-shaped:
// short, definitely-not-business-jargon.
export function relativeDayLabel(daysAgo: number): string {
  if (daysAgo <= 0) return "today";
  if (daysAgo === 1) return "yesterday";
  if (daysAgo < 7) return `${daysAgo} days ago`;
  if (daysAgo < 30) {
    const w = Math.round(daysAgo / 7);
    return w === 1 ? "1 week ago" : `${w} weeks ago`;
  }
  if (daysAgo < 365) {
    const m = Math.round(daysAgo / 30);
    return m === 1 ? "1 month ago" : `${m} months ago`;
  }
  const y = Math.round(daysAgo / 365);
  return y === 1 ? "1 year ago" : `${y} years ago`;
}

// "Goes by 'Bobby' · Jr. · Primary contact · Spouse to Mary Smith"
// builder. Drops empty parts; returns "" when nothing applies (the
// caller decides whether to render the row at all).
export function buildHeroSubLine(parts: {
  preferredName?: string | null;
  legalFirstName?: string | null;
  suffix?: string | null;
  householdRole?: string | null;
  spouseDisplayName?: string | null;
}): string {
  const out: string[] = [];
  // Show "Goes by 'X'" only when the preferred name actually
  // differs from the legal first name. Skip when null/empty.
  if (
    parts.preferredName &&
    parts.preferredName.trim() !== "" &&
    parts.preferredName.trim().toLowerCase() !==
      (parts.legalFirstName ?? "").trim().toLowerCase()
  ) {
    out.push(`Goes by "${parts.preferredName.trim()}"`);
  }
  if (parts.suffix && parts.suffix.trim() !== "") {
    out.push(parts.suffix.trim());
  }
  if (parts.householdRole && parts.householdRole.trim() !== "") {
    out.push(humanizeHouseholdRole(parts.householdRole));
  }
  if (parts.spouseDisplayName && parts.spouseDisplayName.trim() !== "") {
    out.push(`Spouse to ${parts.spouseDisplayName.trim()}`);
  }
  return out.join(" · ");
}

// "primary_contact" → "Primary contact"; "spouse" → "Spouse" etc.
// Nothing fancy — just lowercase-replace + capitalize first letter.
export function humanizeHouseholdRole(role: string): string {
  const r = role.replace(/_/g, " ").trim().toLowerCase();
  if (r.length === 0) return role;
  return r.charAt(0).toUpperCase() + r.slice(1);
}

// "480-555-8142" — the ONE phone display rule for the whole OS (Grant,
// 2026-09-30: phones must be easy to read after input, whatever was typed).
// Pure regex, no libphonenumber. A 10-digit US number (or 11 with a leading
// 1) gets dashes; an extension ("x123", "ext 5", "#7") is kept after it.
// Anything else (international, odd lengths) is returned exactly as stored
// so we never hide or mangle a number we don't understand.
const PHONE_EXT_RE = /^(.*?)\s*(?:x|ext\.?|extension|#)\s*(\d{1,6})\s*$/i;

export function formatPhone(raw: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return raw;
  const extMatch = PHONE_EXT_RE.exec(trimmed);
  const main = extMatch ? extMatch[1]! : trimmed;
  const ext = extMatch ? ` x${extMatch[2]}` : "";
  const digits = main.replace(/\D/g, "");
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}${ext}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}${ext}`;
  }
  return raw;
}

// Tidy-on-blur for phone inputs. DECISION: inputs accept free typing
// (digits, hyphens, spaces, parentheses, dots, +, extensions) and are only
// tidied when the user leaves the box, via onBlur. The earlier per-keystroke
// formatter fought free input and chopped extensions/international numbers.
// Stored value is the tidied string; matching (normalizePhone) strips
// non-digits so either form is harmless downstream.
export function formatPhoneInput(value: string): string {
  return formatPhone(value) ?? value;
}

// tel: href from any stored phone text: digits and a leading + only, and
// an extension is dropped (a tel: link can't dial one).
export function phoneTelHref(raw: string): string {
  const main = PHONE_EXT_RE.exec(raw.trim())?.[1] ?? raw;
  return `tel:${main.replace(/[^\d+]/g, "")}`;
}

// Audit A6: the hero's relationship summary, computed from the
// household's per-company relationship STATES. The old hero showed
// "Not yet a client" whenever client_since dates were missing — even
// for contacts with ACTIVE policies on the same screen (audit finding
// 4.1). State wins now; the date enriches it when known.
export type RelationshipSummary =
  | { kind: "client_since"; yearStarted: number; yearsAsClient: number }
  | { kind: "active_no_date"; companies: string[] }
  | { kind: "past_client" }
  | { kind: "prospect"; companies: string[] }
  | { kind: "not_engaged" };

export function deriveRelationshipSummary(
  companyRelationships: ReadonlyArray<{
    client_since_this_company: string | null;
    relationship_state: string;
    company_display_name?: string | null;
  }>,
  today: Date = new Date(),
): RelationshipSummary {
  const since = deriveClientSince(companyRelationships, today);
  if (since) return { kind: "client_since", ...since };

  const shortNames = (
    rs: ReadonlyArray<{ company_display_name?: string | null }>,
  ) =>
    rs
      .map((r) =>
        (r.company_display_name ?? "").replace(/^Stewardship\s+/, ""),
      )
      .filter(Boolean);

  const active = companyRelationships.filter(
    (r) => r.relationship_state === "active_client",
  );
  if (active.length > 0) {
    return { kind: "active_no_date", companies: shortNames(active) };
  }
  if (
    companyRelationships.some((r) => r.relationship_state === "past_client")
  ) {
    return { kind: "past_client" };
  }
  const prospects = companyRelationships.filter(
    (r) => r.relationship_state === "prospect",
  );
  if (prospects.length > 0) {
    return { kind: "prospect", companies: shortNames(prospects) };
  }
  return { kind: "not_engaged" };
}

// The "Companies" stat tile's sub-line. When no company is ACTIVE it used
// to say "Not yet a client" even for a past client, while the household
// snapshot (which reads the same relationship rows) said "Client since
// 2024" — two pages answering one question differently (Grok audit,
// 2026-10-31, item 8). It now reads the relationship STATE, the same
// source deriveRelationshipSummary uses for the hero, so the two cannot
// disagree: state wins, the date only enriches.
export function companiesTileBreakdown(
  companyRelationships: ReadonlyArray<{
    client_since_this_company: string | null;
    relationship_state: string;
    company_display_name?: string | null;
  }>,
): string {
  const active = companyRelationships.filter(
    (r) => r.relationship_state === "active_client",
  );
  if (active.length > 0) {
    return active.map((c) => c.company_display_name ?? "").filter(Boolean).join(", ");
  }
  const summary = deriveRelationshipSummary(companyRelationships);
  if (summary.kind === "past_client" || summary.kind === "client_since") {
    return summary.kind === "client_since"
      ? `Past client · since ${summary.yearStarted}`
      : "Past client";
  }
  if (summary.kind === "prospect") return "Prospect, not yet a client";
  return "Not yet a client";
}

// Compute years-as-client from the earliest `client_since_this_company`
// across the contact's company relationships. Used by the hero "Client
// since 2019 · 7 years" line.
export function deriveClientSince(
  companyRelationships: ReadonlyArray<{
    client_since_this_company: string | null;
    relationship_state: string;
  }>,
  today: Date = new Date(),
): { yearStarted: number; yearsAsClient: number } | null {
  // Only consider active or past clients (a household that's a
  // prospect or never engaged hasn't "started" being a client yet).
  const dates = companyRelationships
    .filter(
      (r) =>
        r.relationship_state === "active_client" ||
        r.relationship_state === "past_client",
    )
    .map((r) => r.client_since_this_company)
    .filter((d): d is string => Boolean(d));
  if (dates.length === 0) return null;
  // Earliest: lexicographic min works for ISO YYYY-MM-DD.
  const earliest = dates.sort()[0]!;
  const yearStarted = parseInt(earliest.slice(0, 4), 10);
  const yearsAsClient = Math.max(0, today.getUTCFullYear() - yearStarted);
  return { yearStarted, yearsAsClient };
}

// Activity timestamp → "Apr 6 · 2:14 PM". Used by the activity
// timeline date column. Drops the year unless the activity is more
// than a year old.
export function formatActivityTimestamp(
  iso: string,
  today: Date = new Date(),
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getUTCFullYear() === today.getUTCFullYear();
  const dateStr = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
  const timeStr = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${dateStr} · ${timeStr}`;
}
